/* eslint-disable react/no-array-index-key */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Divider,
  Grid,
  Link as MuiLink,
  Breadcrumbs,
  List,
  ListItem,
  ListItemText,
  Stack,
  Tooltip,
  IconButton,
  Skeleton,
  Alert,
  Button,
  ListItemAvatar,
  Avatar,
  TextField,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import NotesIcon from '@mui/icons-material/Notes';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { TableContainerProps as MuiTableContainerProps } from '@mui/material/TableContainer';
import { styled, useTheme } from '@mui/material/styles';
import { Link, useParams } from 'react-router-dom';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { DownloadOutlined } from '@mui/icons-material';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { formatQueryError } from 'renderer/shared/formatters/formatters';
import { useAppSelector } from 'renderer/shared/state/hooks';
import { selectIsAdmin } from 'renderer/features/auth/state/userSelectors';
import {
  useGetTransactionDetailsQuery,
  useUpdateTransactionNoteMutation,
} from '../services/transactions.endpoints';
import ExportPDFView from '../../users-page/components/export-pdf';
import { TransactionDetails } from '../schemas/transaction.schemas';
import {
  getProductDescription,
  transformTransactionToInvoice,
} from '../schemas/transaction.transformer';
import RefundDialog from './RefundDialog';
import CheckoutCartPanel from './CheckoutCartPanel';
import TransactionFailureDetailsPanel from './TransactionFailureDetails';
import TransactionPaymentMethodInfo from './TransactionPaymentMethodInfo';

const PDFIconButton = ({
  transaction,
}: {
  transaction: TransactionDetails;
}) => {
  const theme = useTheme();
  const transactionInfo = transformTransactionToInvoice([transaction]);
  return (
    <PDFDownloadLink
      document={<ExportPDFView transaction={transactionInfo} />}
      fileName={`${transaction._id}.pdf`}
    >
      <Tooltip title="Download" placement="top">
        <IconButton>
          <DownloadOutlined style={{ color: theme.palette.grey[900] }} />
        </IconButton>
      </Tooltip>
    </PDFDownloadLink>
  );
};

const MainContentWrapper = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[3],
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2),
  margin: theme.spacing(2, 0),
}));

interface TableContainerProps extends MuiTableContainerProps {
  component?: React.ElementType;
}

const StyledTableContainer = styled(TableContainer)<TableContainerProps>(
  ({ theme }) => ({
    boxShadow: 'none',
    '& .MuiTableCell-head': {
      backgroundColor: theme.palette.action.hover,
    },
  }),
);

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  boxShadow: 'none',
  borderRadius: 0,
}));

const formatCurrency = (amount: number): string => `$${amount.toFixed(2)}`;

const formatCheckpointTime = (value: string): string =>
  new Date(value).toLocaleString();

const DetailRow = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ minWidth: 56, flexShrink: 0 }}
    >
      {label}
    </Typography>
    <Typography variant="body2" component="div" sx={{ flex: 1 }}>
      {children}
    </Typography>
  </Box>
);

const PaymentDetailsPage: React.FC = () => {
  const { id } = useParams();
  const isAdmin = useAppSelector(selectIsAdmin);
  const { data: transaction, error } = useGetTransactionDetailsQuery(id || '');
  const [updateNote, { isLoading: updateLoading }] =
    useUpdateTransactionNoteMutation();
  const [refundModalOpen, setRefundModalOpen] = useState(false);
  const [editNote, setEditNote] = useState(false);
  const [note, setNote] = useState('');

  const handleEditNoteClick = useCallback(() => {
    if (editNote && transaction) {
      updateNote({ transactionId: transaction._id, note });
      setEditNote(false);
      return;
    }
    setEditNote(true);
  }, [editNote, note, transaction, updateNote]);

  useEffect(() => {
    if (transaction && transaction.note) {
      setNote(transaction.note);
    }
  }, [transaction]);
  // debugger
  if (error) return <Alert severity="error">{formatQueryError(error)}</Alert>;
  console.log('error', error);

  console.log('error', error);

  if (!transaction) return <Skeleton />;

  return (
    <Container>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
        <MuiLink underline="hover" color="inherit" href="/">
          Home
        </MuiLink>
        <MuiLink underline="hover" color="inherit" href="/#/transactions">
          Transactions
        </MuiLink>
        <Typography color="text.primary">Transaction Details</Typography>
      </Breadcrumbs>
      <Typography variant="h5" gutterBottom>
        Transaction Detail
      </Typography>

      <PDFIconButton transaction={transaction} />

      {isAdmin && (
        <Button
          variant="outlined"
          color="error"
          onClick={() => setRefundModalOpen(true)}
        >
          Issue Refund
        </Button>
      )}
      {isAdmin && (
        <RefundDialog
          open={refundModalOpen}
          setOpen={setRefundModalOpen}
          transaction={transaction}
        />
      )}

      <MainContentWrapper>
        <StyledPaper>
          <Typography variant="h6" gutterBottom>
            Overview
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={5}>
              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{ fontWeight: 600 }}
              >
                Customer
              </Typography>
              <Stack spacing={1}>
                <DetailRow label="Name">
                  <>
                    {transaction.firstName} {transaction.lastName}
                    {transaction.userId && (
                      <Link to={`/users/${transaction.userId}`}>
                        <IconButton color="info" size="small" sx={{ ml: 0.5 }}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Link>
                    )}
                  </>
                </DetailRow>
                <DetailRow label="Email">{transaction.email}</DetailRow>
              </Stack>
            </Grid>
            <Grid item xs={12} md={7}>
              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{ fontWeight: 600 }}
              >
                Transaction
              </Typography>
              <Stack spacing={1}>
                <DetailRow label="Date">
                  {transaction.createdAt
                    ? formatCheckpointTime(transaction.createdAt)
                    : 'N/A'}
                </DetailRow>
                <DetailRow label="Status">{transaction.status}</DetailRow>
                {transaction.metadata?.stage &&
                  transaction.status === 'failed' && (
                    <DetailRow label="Failed at">
                      <Typography component="span" color="error">
                        {transaction.metadata.stage}
                      </Typography>
                    </DetailRow>
                  )}
                {transaction.refundedAmount > 0 && (
                  <DetailRow label="Refunded">
                    <Typography component="span" color="error">
                      {formatCurrency(transaction.refundedAmount)}
                    </Typography>
                  </DetailRow>
                )}
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ mb: 2 }} />
              <TransactionPaymentMethodInfo
                transaction={transaction}
                showProviderDashboardLink={isAdmin}
              />
            </Grid>
          </Grid>
        </StyledPaper>
      </MainContentWrapper>

      {transaction.metadata?.checkoutCart && (
        <MainContentWrapper>
          <StyledPaper>
            <CheckoutCartPanel
              checkoutCart={transaction.metadata.checkoutCart}
              currentTransactionId={transaction._id}
              checkoutSessionTransactions={
                transaction.checkoutSessionTransactions
              }
            />
          </StyledPaper>
        </MainContentWrapper>
      )}

      {(transaction.checkpoints?.length ||
        transaction.metadata?.failureDetails) && (
        <MainContentWrapper>
          <StyledPaper>
            <Grid container spacing={3} alignItems="flex-start">
              <Grid item xs={12} lg={6}>
                {transaction.checkpoints &&
                  transaction.checkpoints.length > 0 && (
                    <>
                      <Typography variant="h6" gutterBottom>
                        Purchase checkpoints
                      </Typography>
                      <Stack spacing={1.5}>
                        {transaction.checkpoints.map((checkpoint, index) => (
                          <Box
                            key={`${checkpoint.step}-${index}`}
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 1.5,
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 24,
                                height: 24,
                                mt: '2px',
                                flexShrink: 0,
                              }}
                            >
                              {checkpoint.status === 'failed' ? (
                                <ErrorOutlineIcon
                                  color="error"
                                  fontSize="small"
                                />
                              ) : (
                                <CheckCircleOutlineIcon
                                  color="success"
                                  fontSize="small"
                                />
                              )}
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body1" lineHeight={1.5}>
                                {checkpoint.label}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                lineHeight={1.5}
                              >
                                {formatCheckpointTime(checkpoint.at)}
                              </Typography>
                              {checkpoint.detail && (
                                <Typography
                                  variant="body2"
                                  color={
                                    checkpoint.status === 'failed'
                                      ? 'error'
                                      : 'text.secondary'
                                  }
                                  lineHeight={1.5}
                                >
                                  {checkpoint.detail}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        ))}
                      </Stack>
                    </>
                  )}
              </Grid>
              <Grid item xs={12} lg={6}>
                {transaction.status === 'failed' &&
                  transaction.metadata?.failureDetails && (
                    <TransactionFailureDetailsPanel
                      failureDetails={transaction.metadata.failureDetails}
                      stage={transaction.metadata.stage}
                    />
                  )}
              </Grid>
            </Grid>
          </StyledPaper>
        </MainContentWrapper>
      )}

      <MainContentWrapper sx={{ mt: 5 }}>
        <StyledTableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Product</TableCell>
                <TableCell>Purchase Date</TableCell>
                <TableCell>Starting Price</TableCell>
                <TableCell>Quantity</TableCell>
                <TableCell>Subtotal</TableCell>
                <TableCell>Discount</TableCell>
                <TableCell>Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>{getProductDescription(transaction)}</TableCell>
                <TableCell>
                  {transaction?.createdAt
                    ? new Date(transaction.createdAt).toLocaleDateString()
                    : 'N/A'}
                </TableCell>
                <TableCell>
                  {formatCurrency(transaction.prices?.startingPrice ?? 0)}
                </TableCell>
                <TableCell>{transaction.quantity}</TableCell>
                <TableCell>
                  {formatCurrency(transaction.prices?.subtotal ?? 0)}
                </TableCell>
                <TableCell>
                  {formatCurrency(
                    transaction.prices?.discountDetails?.amount ?? 0,
                  )}
                </TableCell>
                <TableCell>
                  {formatCurrency(transaction?.amount || 0)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </StyledTableContainer>
        <Grid container spacing={2} sx={{ mt: 2 }} direction="row">
          <Grid item xs={12} lg={6}>
            <List>
              <ListItem
                secondaryAction={
                  <IconButton
                    disabled={updateLoading}
                    onClick={handleEditNoteClick}
                  >
                    {editNote ? <SendIcon /> : <EditIcon />}
                  </IconButton>
                }
              >
                <ListItemAvatar>
                  <Avatar>
                    <NotesIcon />
                  </Avatar>
                </ListItemAvatar>
                {editNote ? (
                  <TextField
                    name="note"
                    label="Notes"
                    fullWidth
                    disabled={updateLoading}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  />
                ) : (
                  <ListItemText
                    primary={transaction.note ? 'Notes' : 'No Notes'}
                    secondary={transaction.note}
                  />
                )}
              </ListItem>
            </List>
          </Grid>
          <Grid item xs={12} lg={6}>
            <Box>
              {/* <StyledButton
                variant="contained"
                startIcon={<FileDownloadIcon />}
                onClick={handleDownloadInvoice}
              >
                Download Invoice
              </StyledButton> */}
              {/* <List>
                {transaction?.serials.map((serial, index) => (
                  <ListItem key={`id-${index}`}>
                    <ListItemText primary={`Serial ${index + 1}: ${serial}`} />
                  </ListItem>
                ))}
              </List> */}
              <Typography variant="h6">Serials</Typography>
              <List dense disablePadding>
                {/* ────────────── Bundle path ────────────── */}
                {Array.isArray(transaction.serialGroups) &&
                transaction.serialGroups.length
                  ? transaction.serialGroups.map((g, gIdx) => (
                      <Box key={gIdx} sx={{ mb: 1 }}>
                        {/* add-on label */}
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 500,
                            textDecoration: 'underline',
                            lineHeight: 1.2,
                          }}
                        >
                          {g.displayName ?? g.productName} ({g.version})
                        </Typography>

                        {/* serial list */}
                        <List dense disablePadding sx={{ pl: 2 }}>
                          {g.serials.map((s: string, idx: number) => (
                            <ListItem key={`${gIdx}-${idx}`} sx={{ py: 0 }}>
                              <ListItemText primary={s} />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    ))
                  : /* ──────────── Regular plugin path ──────────── */
                    (transaction?.serials ?? []).map((serial, index) => (
                      <ListItem key={`serial-${index}`} sx={{ py: 0 }}>
                        <ListItemText
                          primary={`Serial ${index + 1}: ${serial}`}
                        />
                      </ListItem>
                    ))}
              </List>
            </Box>
          </Grid>
        </Grid>
      </MainContentWrapper>
    </Container>
  );
};

export default PaymentDetailsPage;
